import { useEffect, useState, useCallback } from 'react';
import { Flex, Text, chakra } from '@chakra-ui/react';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
  onClose: () => void;
}

const typeBgColors = {
  success: 'volyx.success',
  error: 'volyx.error',
  info: 'volyx.primary',
};

const EXIT_DURATION_MS = 220;

export const Toast = ({
  message,
  type = 'info',
  duration = 2800,
  onClose,
}: ToastProps) => {
  const [isExiting, setIsExiting] = useState(false);

  const close = useCallback(() => {
    if (isExiting) return;
    setIsExiting(true);
    setTimeout(onClose, EXIT_DURATION_MS);
  }, [onClose, isExiting]);

  useEffect(() => {
    const timer = setTimeout(close, duration);
    return () => clearTimeout(timer);
  }, [duration, close]);

  return (
    <Flex
      position="fixed"
      top="calc(env(safe-area-inset-top, 0px) + 1.25rem)"
      right="5"
      minW="300px"
      maxW="500px"
      p="4"
      px="5"
      borderRadius="xl"
      boxShadow="lg"
      align="center"
      justify="space-between"
      gap="4"
      zIndex={10000}
      color="white"
      transition="all 0.22s ease-out"
      bg={typeBgColors[type]}
      animation={
        isExiting
          ? 'slideOutRight 0.22s ease-out forwards'
          : 'slideInFromRight 0.3s ease-out'
      }
      opacity={isExiting ? 0 : undefined}
      role="alert"
    >
      <Text flex="1" fontSize="sm" lineHeight="tall">
        {message}
      </Text>
      <chakra.button
        bg="transparent"
        border="none"
        color="white"
        fontSize="2xl"
        lineHeight="1"
        cursor="pointer"
        p="0"
        w="6"
        h="6"
        display="flex"
        alignItems="center"
        justifyContent="center"
        opacity={0.8}
        transition="opacity 0.2s"
        _hover={{ opacity: 1 }}
        _focus={{ outline: '2px solid rgba(255,255,255,0.5)', outlineOffset: '2px' }}
        onClick={close}
        aria-label="Close notification"
      >
        ×
      </chakra.button>
    </Flex>
  );
};
